import api from './api';
import {
  Contact,
  ContactListResponse,
  CreateContactData,
  UpdateContactData,
  ContactStats,
  ContactFilters,
  EnablePortalData,
  AddToListData,
  BulkUpdateData,
  MergeContactsData
} from '../types/contact';

export const contactService = {
  // Get all contacts
  getAll: async (
    workspaceId: string,
    filters?: ContactFilters,
    page?: number,
    limit?: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ): Promise<ContactListResponse> => {
    const params: any = {
      page: page || 1,
      limit: limit || 20,
      sortBy: sortBy || 'lastName',
      sortOrder: sortOrder || 'asc',
      ...filters
    };

    // Convert array filters to comma-separated strings
    if (filters?.contactType && Array.isArray(filters.contactType)) {
      params.contactType = filters.contactType.join(',');
    }

    const response = await api.get(`/workspaces/${workspaceId}/contacts`, { params });
    return response.data;
  },

  // Get contact by ID
  getById: async (
    workspaceId: string,
    contactId: string,
    include?: string[]
  ): Promise<{ contact: Contact; deals?: any[] }> => {
    const params: any = {};
    if (include && include.length > 0) {
      params.include = include.join(',');
    }

    const response = await api.get(`/workspaces/${workspaceId}/contacts/${contactId}`, { params });
    return response.data;
  },

  // Create contact
  create: async (workspaceId: string, data: CreateContactData): Promise<{ contact: Contact; message: string }> => {
    const response = await api.post(`/workspaces/${workspaceId}/contacts`, data);
    return response.data;
  },

  // Update contact
  update: async (
    workspaceId: string,
    contactId: string,
    data: UpdateContactData
  ): Promise<{ contact: Contact; message: string }> => {
    const response = await api.put(`/workspaces/${workspaceId}/contacts/${contactId}`, data);
    return response.data;
  },

  // Delete contact (soft delete)
  delete: async (workspaceId: string, contactId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/workspaces/${workspaceId}/contacts/${contactId}`);
    return response.data;
  },

  // Enable portal access
  enablePortalAccess: async (
    workspaceId: string,
    contactId: string,
    data: EnablePortalData
  ): Promise<{ contact: Contact; message: string }> => {
    const response = await api.post(`/workspaces/${workspaceId}/contacts/${contactId}/enable-portal`, data);
    return response.data;
  },

  // Reset password
  resetPassword: async (
    workspaceId: string,
    contactId: string
  ): Promise<{ contact: Contact; message: string }> => {
    const response = await api.post(`/workspaces/${workspaceId}/contacts/${contactId}/reset-password`);
    return response.data;
  },

  // Add contact to list
  addToList: async (
    workspaceId: string,
    contactId: string,
    data: AddToListData
  ): Promise<{ contact: Contact; message: string }> => {
    const response = await api.post(`/workspaces/${workspaceId}/contacts/${contactId}/add-to-list`, data);
    return response.data;
  },

  // Remove contact from list
  removeFromList: async (
    workspaceId: string,
    contactId: string,
    data: AddToListData
  ): Promise<{ contact: Contact; message: string }> => {
    const response = await api.post(`/workspaces/${workspaceId}/contacts/${contactId}/remove-from-list`, data);
    return response.data;
  },

  // Bulk update contacts
  bulkUpdate: async (
    workspaceId: string,
    data: BulkUpdateData
  ): Promise<{ message: string; modifiedCount: number }> => {
    const response = await api.put(`/workspaces/${workspaceId}/contacts/bulk-update`, data);
    return response.data;
  },

  // Get contact statistics
  getStats: async (workspaceId: string): Promise<{ stats: ContactStats }> => {
    const response = await api.get(`/workspaces/${workspaceId}/contacts/stats`);
    return response.data;
  },

  // Create contact from registrant
  createFromRegistrant: async (
    workspaceId: string,
    registrantId: string,
    data: { additionalNotes?: string; assignTo?: string }
  ): Promise<{ contact: Contact; message: string }> => {
    const response = await api.post(`/workspaces/${workspaceId}/contacts/from-registrant/${registrantId}`, data);
    return response.data;
  },

  // Merge contacts
  mergeContacts: async (
    workspaceId: string,
    data: MergeContactsData
  ): Promise<{ contact: Contact; message: string; mergedCount: number }> => {
    const response = await api.post(`/workspaces/${workspaceId}/contacts/merge`, data);
    return response.data;
  },

  // Bulk import contacts
  bulkImport: async (
    workspaceId: string,
    contacts: CreateContactData[]
  ): Promise<{ message: string; imported: number; failed: number; errors?: any[] }> => {
    const response = await api.post(`/workspaces/${workspaceId}/contacts/bulk-import`, { contacts });
    return response.data;
  }
};
